import { EllipsisOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, Layout, PageHeader, Table } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useContext, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Notification } from 'app/components/commons/Notification/Notification';
import {
  PUBLISH_ERROR_TYPE,
  PublishModalError,
} from 'app/components/commons/PublishModalError/PublishModalError';
import { PaginatedSearchHeader } from 'app/components/commons/Range/PaginatedSearchHeader/PaginatedSearchHeader';
import { Pagination } from 'app/components/commons/Range/Pagination/Pagination';
import {
  IsLegacyFilterOption,
  ViewModeRadio,
} from 'app/components/commons/ViewModeRadio/ViewModeRadio';
import { PackStatusCell } from 'app/components/lists/cells';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import { UserContext } from 'app/context/UserContext/UserContext';
import { ENDPOINTS } from 'app/hooks/data/core/admin/packs/';
import { useHidePack } from 'app/hooks/data/core/admin/packs/useHidePack';
import { usePacks } from 'app/hooks/data/core/admin/packs/usePacks';
import { usePublishPack } from 'app/hooks/data/core/admin/packs/usePublishPack';
import { useSetupPack } from 'app/hooks/data/core/admin/packs/useSetupPack';
import { useUnpublishPack } from 'app/hooks/data/core/admin/packs/useUnpublishPack';
import {
  useHideOldPackage,
  usePublishOldPackage,
} from 'app/hooks/data/packages/useSavePackage';
import { PackStatus } from 'app/typings/core/admin/packs/enum/pack';
import type { PackListElement } from 'app/typings/core/admin/packs/packList';
import { HOTEL_STATUS } from 'app/typings/core/common/enum/hotelStatus';
import { ApiError } from 'app/typings/error';
import { dateAndHour } from 'app/utils/dates';
import type { PackageErrorCode } from 'app/utils/errors/PackageErrors';
import { retrievePackageErrorMessage } from 'app/utils/errors/PackageErrors';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import type { PackStatusAction } from 'app/utils/packStatus';
import {
  PACK_STATUS_LABELS,
  canApplyPackStatusAction,
} from 'app/utils/packStatus';
import { extractMissingFields } from 'app/utils/publishError';
import { useQueryParamHistory } from 'app/utils/queryParamHistory';
import type { SortOrder } from 'app/utils/sort';
import {
  convertToAntdSortOrder,
  formatSortingForAPI,
  getSortOrder,
} from 'app/utils/sort';

import 'styles/layout/page-list.scss';

import { ModalCheckBox } from '../Extranet/ExtranetBookings/ModalCheckBox';

import { NewPackModal } from './NewPackModal/NewPackModal';
import './Packs.scss';
import { PublishConfirmModal } from './PublishConfirmModal/PublishConfirmModal';

const { Content } = Layout;
const { Column } = Table;

enum SortingColum {
  UPDATED_AT = 'UPDATED_AT',
  NAME_FOR_HOTEL = 'NAME_FOR_HOTEL',
  NAME_FOR_USER = 'NAME_FOR_USER',
}

export type PacksListQueryParam = {
  search?: string;
  pageNumber?: number;
  sortingColumn?: SortingColum;
  sortingOrder?: SortOrder;
  publishedFilter?: PackStatus[];
  isLegacyFilter?: IsLegacyFilterOption;
  isDaytimeFilter?: boolean[];
};

const ALL_PACK_STATUSES = [
  PackStatus.PUBLISHED,
  PackStatus.SETUP,
  PackStatus.UNPUBLISHED,
  PackStatus.HIDDEN,
];

const NEW_STATUS_BY_ACTION: Record<PackStatusAction, PackStatus> = {
  publish: PackStatus.PUBLISHED,
  setup: PackStatus.SETUP,
  unpublish: PackStatus.UNPUBLISHED,
  hide: PackStatus.HIDDEN,
};

const formatPackStatus = (
  statuses?: PackStatus[]
): PackStatus[] | undefined => {
  if (!statuses || statuses.length === 0) {
    return undefined;
  }

  if (ALL_PACK_STATUSES.every((status) => statuses.includes(status))) {
    return undefined;
  }

  return statuses;
};

const queryParamInit: PacksListQueryParam = {
  pageNumber: 1,
  sortingColumn: SortingColum.UPDATED_AT,
  sortingOrder: 'descend',
  publishedFilter: ALL_PACK_STATUSES,
  isLegacyFilter: IsLegacyFilterOption.CURRENT,
};

const queryParamKeys = {
  pageNumber: 'p',
  dateSorting: 'ds',
  publishedFilter: 'pu',
  sortingColumn: 'sc',
  sortingOrder: 'so',
  search: 's',
  isLegacyFilter: 'il',
  isDaytimeFilter: 'dt',
};
const queryParamFormatter = {};
const queryParamExtractor = {
  pageNumber: parseInt,
  nameSorting: getSortOrder,
  updatedAtSorting: getSortOrder,
  publishedFilter: (val: string) =>
    val
      .split(',')
      .map((v) => v.toUpperCase())
      .filter((v) =>
        ALL_PACK_STATUSES.includes(v as PackStatus)
      ) as PackStatus[],
  isDaytimeFilter: (val: string) =>
    val
      .split(',')
      .map((v) => v === 'true')
      .filter((v) => [true, false].includes(v as boolean)) as boolean[],
};

export const PacksList = () => {
  const { isSuperAdmin } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const config = useContext(ConfigContext);
  const [statusFilter, setStatusFilter] =
    useState<PackStatus[]>(ALL_PACK_STATUSES);
  const [isDaytimeFilter, setIsDayTimeFilter] = useState<boolean[]>([
    true,
    false,
  ]);

  const { mutateAsync: hideOldPack } = useHideOldPackage();
  const { mutateAsync: hidePack } = useHidePack();
  const { mutateAsync: publishOldPack, error: publishError } =
    usePublishOldPackage();
  const { mutateAsync: publishPack } = usePublishPack();
  const { mutateAsync: setupPack } = useSetupPack();
  const { mutateAsync: unpublishPack } = useUnpublishPack();
  const queryClient = useQueryClient();

  const [isNewPackModalOpen, setIsNewPackModalOpen] = useState(false);
  const [missingFieldsError, setMissingFieldsError] = useState<string[] | null>(
    null
  );
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const isInstanceOfApiError = publishError instanceof ApiError;

  const publishedError =
    isInstanceOfApiError && publishError.jsonObject
      ? retrievePackageErrorMessage(
          publishError.jsonObject.errorCode as PackageErrorCode
        )
      : { errorTitle: '', errorMessage: '' };

  const { queryParam, updateQueryParam } =
    useQueryParamHistory<PacksListQueryParam>(
      queryParamInit,
      queryParamKeys,
      queryParamFormatter,
      queryParamExtractor
    );

  const pageNumber = queryParam.pageNumber || 1;

  const debouncedQueryParams = useDebounce(queryParam, 500);

  const payload = useMemo(
    () => ({
      pageNumber,
      search: debouncedQueryParams.search,
      sortingColumn: debouncedQueryParams.sortingColumn,
      sortingOrder: debouncedQueryParams.sortingOrder
        ? formatSortingForAPI(debouncedQueryParams.sortingOrder)
        : undefined,
      status: formatPackStatus(debouncedQueryParams.publishedFilter),
      isLegacyFilter: debouncedQueryParams.isLegacyFilter,
      isDaytimeFilter: debouncedQueryParams.isDaytimeFilter,
    }),
    [
      pageNumber,
      debouncedQueryParams.search,
      debouncedQueryParams.sortingColumn,
      debouncedQueryParams.sortingOrder,
      debouncedQueryParams.publishedFilter,
      debouncedQueryParams.isLegacyFilter,
      debouncedQueryParams.isDaytimeFilter,
    ]
  );

  const queryKey = useMemo(() => [ENDPOINTS.getPacks, payload], [payload]);

  const { data: packs } = usePacks(payload);

  const selectedIsLegacyFilterOption =
    queryParam.isLegacyFilter ?? IsLegacyFilterOption.ALL;

  const handleSearch = (search: string) => {
    updateQueryParam({
      search: search,
      pageNumber: 1,
    });
  };

  const handlePublishedStatus = (statuses: PackStatus[]) => {
    const isAllSelected = ALL_PACK_STATUSES.every((s) => statuses.includes(s));

    updateQueryParam({
      publishedFilter:
        statuses.length === 0 || isAllSelected ? undefined : statuses,
      pageNumber: 1,
    });

    setStatusFilter(statuses);
  };

  const handleDayTimeFilter = (filters: boolean[]) => {
    const allFilters = [true, false];
    const isAllSelected = allFilters.every((f) => filters.includes(f));

    updateQueryParam({
      isDaytimeFilter:
        filters.length === 0 || isAllSelected ? undefined : filters,
      pageNumber: 1,
    });

    setIsDayTimeFilter(filters);
  };

  const tableOnChange = (
    __: unknown,
    ___: unknown,
    sorter: SorterResult<PackListElement> | SorterResult<PackListElement>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!singleSorter) {
      return;
    }

    const { order, columnKey } = singleSorter;

    updateQueryParam({
      sortingColumn: columnKey as PacksListQueryParam['sortingColumn'],
      sortingOrder: order as SortOrder,
      pageNumber: 1,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const handlePageNumber = (pageNumber: number) =>
    updateQueryParam({ pageNumber });

  const goToPackEditPage = (id: number, isLegacy: boolean) => {
    const pathname = isLegacy ? `/packs/legacy/${id}` : `/packs/${id}`;

    history.push({
      pathname,
      state: { pathname: location.pathname + location.search },
    });
  };

  const handleRowActions = ({ id, isLegacy }: PackListElement) => ({
    onClick: () => goToPackEditPage(id, isLegacy),
  });

  const renderHeader = () => (
    <PaginatedSearchHeader
      onSearch={handleSearch}
      currentSearch={queryParam.search}
      currentPage={queryParam.pageNumber || 1}
      setPageNumber={handlePageNumber}
      isLastPage={packs?.meta?.isLastPage ?? false}
    />
  );

  const [pendingPublishPack, setPendingPublishPack] =
    useState<PackListElement | null>(null);

  const updateCachedStatus = (packId: number, newStatus: PackStatus) => {
    queryClient.setQueryData<
      { data: PackListElement[] } & Record<string, unknown>
    >(queryKey, (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        data: oldData.data.map((p: PackListElement) =>
          p.id === packId ? { ...p, status: newStatus } : p
        ),
      };
    });
  };

  const mutationByAction: Record<
    PackStatusAction,
    (packId: number) => Promise<void>
  > = {
    publish: publishPack,
    setup: setupPack,
    unpublish: unpublishPack,
    hide: hidePack,
  };

  const applyStatusAction = async (
    pkg: PackListElement,
    action: PackStatusAction
  ) => {
    try {
      await mutationByAction[action](pkg.id);
      updateCachedStatus(pkg.id, NEW_STATUS_BY_ACTION[action]);
    } catch (err) {
      const missingFields = extractMissingFields(err);

      if (missingFields !== null) {
        setMissingFieldsError(missingFields);
        setIsErrorModalVisible(true);
      }
    }
  };

  const handlePublish = async (pkg: PackListElement) => {
    if (
      pkg.status !== PackStatus.PUBLISHED &&
      pkg.hotelStatus !== HOTEL_STATUS.PUBLISHED
    ) {
      setPendingPublishPack(pkg);

      return;
    }

    await doPublish(pkg);
  };

  const doPublish = async (pkg: PackListElement) => {
    if (!pkg.isLegacy) {
      await applyStatusAction(pkg, 'publish');

      return;
    }

    // Legacy packages only know published / unpublished, so the entry toggles.
    const published = pkg.status !== PackStatus.PUBLISHED;

    try {
      await publishOldPack({ packageId: pkg.id, published });
      updateCachedStatus(
        pkg.id,
        published ? PackStatus.PUBLISHED : PackStatus.UNPUBLISHED
      );
    } catch (err) {
      const missingFields = extractMissingFields(err);

      if (missingFields !== null) {
        setMissingFieldsError(missingFields);
        setIsErrorModalVisible(true);
      }
    }
  };

  const handleHide = async (pkg: PackListElement) => {
    if (!pkg.isLegacy) {
      await applyStatusAction(pkg, 'hide');

      return;
    }

    const hidden = pkg.status !== PackStatus.HIDDEN;

    await hideOldPack({ packageId: pkg.id, hidden });
    updateCachedStatus(
      pkg.id,
      hidden ? PackStatus.HIDDEN : PackStatus.UNPUBLISHED
    );
  };

  const newPacksButton = (
    <>
      <Button
        type="primary"
        size="large"
        onClick={() => {
          setIsNewPackModalOpen(true);
        }}
      >
        New
      </Button>
      <NewPackModal
        open={isNewPackModalOpen}
        onClose={() => setIsNewPackModalOpen(false)}
      />
    </>
  );

  const statusMenuItems = (pkg: PackListElement) => {
    if (pkg.isLegacy) {
      return [
        {
          key: 'publish',
          label: pkg.status === PackStatus.PUBLISHED ? 'Unpublish' : 'Publish',
          disabled: pkg.status === PackStatus.HIDDEN,
          onClick: async () => handlePublish(pkg),
        },
        {
          key: 'hide',
          label: pkg.status === PackStatus.HIDDEN ? 'Show' : 'Hide',
          disabled: pkg.status === PackStatus.PUBLISHED || !isSuperAdmin,
          onClick: async () => handleHide(pkg),
        },
      ];
    }

    return [
      {
        key: 'publish',
        label: PACK_STATUS_LABELS[PackStatus.PUBLISHED],
        disabled: !canApplyPackStatusAction('publish', pkg.status),
        onClick: async () => handlePublish(pkg),
      },
      {
        key: 'setup',
        label: PACK_STATUS_LABELS[PackStatus.SETUP],
        disabled: !canApplyPackStatusAction('setup', pkg.status),
        onClick: async () => applyStatusAction(pkg, 'setup'),
      },
      {
        key: 'unpublish',
        label: PACK_STATUS_LABELS[PackStatus.UNPUBLISHED],
        disabled: !canApplyPackStatusAction('unpublish', pkg.status),
        onClick: async () => applyStatusAction(pkg, 'unpublish'),
      },
      {
        key: 'hide',
        label: PACK_STATUS_LABELS[PackStatus.HIDDEN],
        disabled:
          !canApplyPackStatusAction('hide', pkg.status) || !isSuperAdmin,
        onClick: async () => handleHide(pkg),
      },
    ];
  };

  const actionMenu = (pkg: PackListElement) => (
    <div className="actions-menu">
      <Dropdown
        menu={{
          items: [
            ...(pkg.url && pkg.isLegacy
              ? [
                  {
                    key: 'preview',
                    label: 'Preview',
                    // TODO : Change when url will be manage with HotelMode is Offer
                    onClick: () =>
                      window.open(`${config?.appUrl}/fr/${pkg.url}`),
                  },
                ]
              : []),
            {
              key: 'edit',
              label: 'Edit',
              onClick: () => handleRowActions(pkg).onClick(),
            },
            {
              key: 'change-status',
              type: 'group' as const,
              label: 'Change status',
              children: statusMenuItems(pkg),
            },
          ],
        }}
        trigger={['click']}
      >
        <EllipsisOutlined rotate={90} />
      </Dropdown>
    </div>
  );

  return (
    <Layout className="page-list admin-packs">
      <PageHeader className="header" title="Packages" extra={newPacksButton} />
      <Content className="body">
        <Table<PackListElement>
          dataSource={packs?.data ?? []}
          pagination={false}
          title={renderHeader}
          loading={false}
          rowKey={(record) => `${record.id}-${record.isLegacy ? 'old' : 'new'}`}
          onRow={handleRowActions}
          onChange={tableOnChange}
        >
          <Column
            key={SortingColum.NAME_FOR_HOTEL}
            title="Name hotel"
            dataIndex="nameForHotel"
            sorter
            sortOrder={
              queryParam.sortingColumn === SortingColum.NAME_FOR_HOTEL
                ? convertToAntdSortOrder(queryParam.sortingOrder)
                : undefined
            }
          />
          <Column
            key={SortingColum.NAME_FOR_USER}
            title="Name user"
            dataIndex="nameForUser"
            sorter
            sortOrder={
              queryParam.sortingColumn === SortingColum.NAME_FOR_USER
                ? convertToAntdSortOrder(queryParam.sortingOrder)
                : undefined
            }
          />
          <Column
            key="hotelName"
            title="Hotel"
            dataIndex="hotelName"
            width={350}
          />
          <Column<PackListElement>
            title="Data Model"
            dataIndex="isLegacy"
            filterDropdown={() => (
              <ViewModeRadio<IsLegacyFilterOption>
                value={selectedIsLegacyFilterOption}
                onChange={(selected) => {
                  updateQueryParam({
                    isLegacyFilter: selected,
                    pageNumber: 1,
                  });
                }}
                options={[
                  {
                    value: IsLegacyFilterOption.CURRENT,
                    label: 'Current hotel mode',
                  },
                  {
                    value: IsLegacyFilterOption.ALL,
                    label: 'All hotel modes',
                  },
                ]}
              />
            )}
            render={(isLegacy: boolean) => (isLegacy ? 'Old' : 'New')}
          />
          <Column<PackListElement>
            title={
              <ModalCheckBox
                title="Nightstay / Day time"
                onChange={handleDayTimeFilter}
                items={[
                  {
                    label: 'Nightstay',
                    value: false,
                  },
                  {
                    label: 'Day time',
                    value: true,
                  },
                ]}
                isCenter={false}
                value={isDaytimeFilter}
              />
            }
            dataIndex="isDaytime"
            render={(isDaytime: boolean) =>
              isDaytime ? 'Day time' : 'Nightstay'
            }
          />
          <Column
            key={SortingColum.UPDATED_AT}
            title="Last modified"
            dataIndex="updatedAt"
            sorter
            sortOrder={
              queryParam.sortingColumn === SortingColum.UPDATED_AT
                ? convertToAntdSortOrder(queryParam.sortingOrder)
                : undefined
            }
            render={dateAndHour}
          />
          <Column
            key="status"
            title={
              <ModalCheckBox
                title="Published"
                onChange={handlePublishedStatus}
                items={ALL_PACK_STATUSES.map((status) => ({
                  label: PACK_STATUS_LABELS[status],
                  value: status,
                }))}
                isCenter={false}
                value={statusFilter}
              />
            }
            dataIndex="status"
            render={(status: PackStatus) => PackStatusCell(status)}
          />
          <Column
            render={actionMenu}
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
        </Table>
        <Pagination
          className="footer"
          currentPage={queryParam.pageNumber || 1}
          setPageNumber={handlePageNumber}
          isLastPage={packs?.meta?.isLastPage ?? false}
        />
        <PublishModalError
          type={PUBLISH_ERROR_TYPE.PACKAGE}
          missingFields={missingFieldsError ?? []}
          isOpen={isErrorModalVisible}
          handleOk={() => setIsErrorModalVisible(false)}
        />
        <PublishConfirmModal
          pkg={pendingPublishPack}
          onConfirm={async (pkg) => {
            setPendingPublishPack(null);
            await doPublish(pkg);
          }}
        />
      </Content>
      <Notification
        title={publishedError.errorTitle}
        content={publishedError.errorMessage}
        visible={isInstanceOfApiError}
        type="error"
        hasCancelButton={false}
        centered
      />
    </Layout>
  );
};
